/**
 * De agenda van één woning, en de blokkades die de verhuurder er zelf op zet.
 *
 * De routes hiervoor bestaan sinds stage 1; het scherm ontbrak. Dat betekende dat
 * een verhuurder die zijn woning twee weken zelf wilde gebruiken, daar niets aan
 * kon doen behalve een nepboeking maken — en dat vervuilt de omzet, de fee en de
 * verhuurdagenteller alle drie.
 *
 * Twee dingen die dit scherm uit elkaar houdt en die van buiten op elkaar lijken:
 *
 * 1. **Een blokkade is geen reservering.** Hij heeft geen gast, geen prijs en geen
 *    bevestiging. Hij houdt alleen dezelfde periode bezet, via dezelfde
 *    `EXCLUDE`-constraint, en telt in het nachtgrootboek apart mee (§17.28).
 * 2. **Een reservering wordt hier niet weggehaald.** Wie een boeking kwijt wil,
 *    annuleert hem op het reserveringenscherm — met de gevolgen voor de aanspraak
 *    van de gast die daarbij horen. De API weigert het ook, en dit scherm zegt het
 *    vooraf in plaats van de verhuurder tegen die weigering aan te laten lopen.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { FormEvent, ReactElement } from 'react'
import { useFormatters } from '@siteshowroom/i18n/react'

import { MonthCalendar, stateOf } from '../components/month-calendar'
import { useLocale, useTranslate } from '../lib/i18n-provider'
import { listResidences } from '../lib/media-client'
import type { ResidenceOption } from '../lib/media-client'
import { monthStart } from '../lib/overview-client'
import {
  BLOCK_KINDS,
  availabilityErrorMessageKey,
  createBlock,
  listAvailability,
  removeBlock,
} from '../lib/availability-client'
import type {
  AvailabilityFailure,
  AvailabilityResult,
  BlockKind,
  Stay,
} from '../lib/availability-client'

type State =
  | { readonly phase: 'loading' }
  | { readonly phase: 'ready'; readonly stays: readonly Stay[] }
  | { readonly phase: 'failed'; readonly error: AvailabilityFailure }

function FailureNotice({ error }: { readonly error: AvailabilityFailure }): ReactElement {
  const t = useTranslate()
  return (
    <div className="notice notice--error" role="alert">
      <p className="notice__title">{t(availabilityErrorMessageKey(error))}</p>
      <code className="notice__detail">{error.message}</code>
    </div>
  )
}

export function CalendarPage(): ReactElement {
  const t = useTranslate()
  const { locale } = useLocale()
  const formatters = useFormatters()

  const [residences, setResidences] = useState<readonly ResidenceOption[]>([])
  const [residenceId, setResidenceId] = useState<string | null>(null)
  const [monthOffset, setMonthOffset] = useState(0)
  const [state, setState] = useState<State>({ phase: 'loading' })
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<AvailabilityFailure | null>(null)

  const controllerRef = useRef<AbortController | null>(null)

  const today = new Date()
  const from = monthStart(today, monthOffset)
  const to = monthStart(today, monthOffset + 1)

  useEffect(() => {
    let cancelled = false
    void listResidences().then((result) => {
      if (cancelled || !result.ok) {
        return
      }
      setResidences(result.data)
      const first = result.data[0]
      setResidenceId(first === undefined ? null : first.id)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const load = useCallback(async (): Promise<void> => {
    if (residenceId === null) {
      return
    }
    controllerRef.current?.abort()
    const controller = new AbortController()
    controllerRef.current = controller

    const result = await listAvailability(residenceId, { from, to }, { signal: controller.signal })
    if (controller.signal.aborted) {
      return
    }
    setState(
      result.ok ? { phase: 'ready', stays: result.data } : { phase: 'failed', error: result.error },
    )
  }, [residenceId, from, to])

  useEffect(() => {
    if (residenceId === null) {
      return
    }
    // Zelfde patroon als op de andere schermen: de begintoestand is al 'loading',
    // state wordt pas ná de await gezet en de cleanup breekt de aanvraag af.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setState({ phase: 'loading' })
    void load()
    return () => {
      controllerRef.current?.abort()
    }
  }, [residenceId, load])

  const run = useCallback(
    async (work: () => Promise<AvailabilityResult<unknown>>): Promise<void> => {
      setBusy(true)
      setActionError(null)
      const result = await work()
      if (!result.ok) {
        setActionError(result.error)
      }
      await load()
      setBusy(false)
    },
    [load],
  )

  const stays = state.phase === 'ready' ? state.stays : []
  // Alleen wat de verhuurder zelf kan weghalen; reserveringen horen op het
  // reserveringenscherm en niet in deze lijst.
  const blocks = stays.filter((stay) => stateOf(stay) === 'blocked')

  return (
    <section className="page" aria-labelledby="calendar-heading">
      <div className="page__header">
        <h1 className="page__title" id="calendar-heading">
          {t('dashboard.availability.title')}
        </h1>
        <button
          className="button"
          disabled={residenceId === null || busy}
          onClick={() => {
            setState({ phase: 'loading' })
            void load()
          }}
          type="button"
        >
          {t('common.action.reload')}
        </button>
      </div>

      <p className="prose">{t('dashboard.availability.intro')}</p>

      {residences.length > 1 && (
        <label className="field field--inline">
          <span className="field__label">{t('dashboard.media.residence')}</span>
          <select
            className="field__input"
            disabled={busy}
            onChange={(event) => {
              setResidenceId(event.target.value)
            }}
            value={residenceId ?? ''}
          >
            {residences.map((residence) => (
              <option key={residence.id} value={residence.id}>
                {residence.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {residences.length === 0 && (
        <p className="page__status" role="status">
          {t('dashboard.media.noResidence')}
        </p>
      )}

      {residenceId !== null && (
        <>
          <div className="period">
            <button
              className="button button--quiet"
              onClick={() => {
                setMonthOffset((current) => current - 1)
              }}
              type="button"
            >
              {t('dashboard.overview.period.previous')}
            </button>
            <span className="period__label">
              {formatters.formatDate(`${from}T00:00:00Z`, {
                timeZone: 'UTC',
                year: 'numeric',
                month: 'long',
              })}
            </span>
            <button
              className="button button--quiet"
              onClick={() => {
                setMonthOffset((current) => current + 1)
              }}
              type="button"
            >
              {t('dashboard.overview.period.next')}
            </button>
            {monthOffset !== 0 && (
              <button
                className="button button--quiet"
                onClick={() => {
                  setMonthOffset(0)
                }}
                type="button"
              >
                {t('dashboard.overview.period.today')}
              </button>
            )}
          </div>

          {state.phase === 'loading' && (
            <p className="page__status" role="status">
              {t('common.state.loading')}
            </p>
          )}
          {state.phase === 'failed' && <FailureNotice error={state.error} />}
          {actionError !== null && <FailureNotice error={actionError} />}

          {state.phase === 'ready' && (
            <MonthCalendar from={from} locale={locale} stays={state.stays} />
          )}

          <BlockForm
            busy={busy}
            defaultFrom={from}
            onCreate={(input) => {
              void run(() => createBlock(residenceId, input))
            }}
          />

          <h2 className="page__sectionTitle">{t('dashboard.availability.blocks.title')}</h2>
          {blocks.length === 0 ? (
            <p className="page__status" role="status">
              {t('dashboard.availability.blocks.empty')}
            </p>
          ) : (
            <ul className="block-list">
              {blocks.map((block) => (
                <li className="block-list__item" key={block.id}>
                  <span className="block-list__period">
                    {formatters.formatDate(`${block.checkIn}T00:00:00Z`, { timeZone: 'UTC' })} –{' '}
                    {formatters.formatDate(`${block.checkOut}T00:00:00Z`, { timeZone: 'UTC' })}
                  </span>
                  <span className="block-list__kind">
                    {t(`dashboard.availability.kind.${block.kind}`)}
                  </span>
                  {/* Notitie van de verhuurder: data, geen interfacetekst. */}
                  {block.note !== '' && <span className="block-list__note">{block.note}</span>}
                  <button
                    className="button button--danger"
                    disabled={busy}
                    onClick={() => {
                      void run(() => removeBlock(residenceId, block.id))
                    }}
                    type="button"
                  >
                    {t('dashboard.availability.blocks.remove')}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  )
}

interface BlockFormProps {
  readonly busy: boolean
  /** Eerste dag van de zichtbare maand; het formulier begint waar de agenda staat. */
  readonly defaultFrom: string
  readonly onCreate: (input: {
    readonly checkIn: string
    readonly checkOut: string
    readonly kind: BlockKind
    readonly note: string
  }) => void
}

function BlockForm({ busy, defaultFrom, onCreate }: BlockFormProps): ReactElement {
  const t = useTranslate()

  const [checkIn, setCheckIn] = useState(defaultFrom)
  const [checkOut, setCheckOut] = useState('')
  const [kind, setKind] = useState<BlockKind>('owner_block')
  const [note, setNote] = useState('')

  function submit(event: FormEvent): void {
    event.preventDefault()
    if (checkIn === '' || checkOut === '') {
      return
    }
    onCreate({ checkIn, checkOut, kind, note: note.trim() })
    setNote('')
  }

  return (
    <form className="block-form" onSubmit={submit}>
      <h2 className="page__sectionTitle">{t('dashboard.availability.add.title')}</h2>
      {/* Half-open, net als in de database: de einddatum is de eerste dag die weer
          vrij is. Dat staat erbij, want het is de vergissing die iedereen één keer
          maakt. */}
      <p className="prose">{t('dashboard.availability.add.explanation')}</p>

      <div className="block-form__row">
        <label className="field">
          <span className="field__label">{t('dashboard.availability.field.checkIn')}</span>
          <input
            className="field__input"
            disabled={busy}
            onChange={(event) => {
              setCheckIn(event.target.value)
            }}
            required
            type="date"
            value={checkIn}
          />
        </label>

        <label className="field">
          <span className="field__label">{t('dashboard.availability.field.checkOut')}</span>
          <input
            className="field__input"
            disabled={busy}
            min={checkIn}
            onChange={(event) => {
              setCheckOut(event.target.value)
            }}
            required
            type="date"
            value={checkOut}
          />
        </label>

        <label className="field">
          <span className="field__label">{t('dashboard.availability.field.kind')}</span>
          <select
            className="field__input"
            disabled={busy}
            onChange={(event) => {
              const next = BLOCK_KINDS.find((candidate) => candidate === event.target.value)
              if (next !== undefined) {
                setKind(next)
              }
            }}
            value={kind}
          >
            {BLOCK_KINDS.map((candidate) => (
              <option key={candidate} value={candidate}>
                {t(`dashboard.availability.kind.${candidate}`)}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span className="field__label">{t('dashboard.availability.field.note')}</span>
          <input
            className="field__input"
            disabled={busy}
            maxLength={2000}
            onChange={(event) => {
              setNote(event.target.value)
            }}
            placeholder={t('dashboard.availability.field.notePlaceholder')}
            type="text"
            value={note}
          />
        </label>
      </div>

      <button className="button button--primary" disabled={busy} type="submit">
        {t('dashboard.availability.add.submit')}
      </button>
    </form>
  )
}
